/* User-facing release notes. Content and audience filtering belong to /api/updates.
 * One provider per authenticated app; no localStorage fallback for account state. */
(function () {
  /* Expressive shell only (plan-expressive-inner-pages.md §2(c)-7, owned by P5): overrides
   * portal-updates.css (Bento-shared, untouched). GxCore.injectCss is a no-op under Bento;
   * every selector starts with html[data-ui-shell="expressive"] (tests/helpers/gx_css_guard.cjs). */
  GxCore.injectCss('gx-css-p-updates', String.raw`
html[data-ui-shell="expressive"] .gs-update-banner{border-bottom:0;background:var(--gx-pc);color:var(--gx-on)}
html[data-ui-shell="expressive"] .gs-update-banner p{color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .gs-update-banner a{font-weight:700;color:var(--gx-p)}
html[data-ui-shell="expressive"] .gs-update-banner button{border-radius:12px;color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .gs-update-label{letter-spacing:0;font-size:13px;font-weight:700;color:var(--gx-p)}
html[data-ui-shell="expressive"] .gs-updates{color:var(--gx-on);font-family:var(--gx-font)}
html[data-ui-shell="expressive"] .gs-updates-heading{border-bottom:0;padding:0 0 8px}
html[data-ui-shell="expressive"] .gs-updates-heading .gs-update-label{margin-bottom:4px}
html[data-ui-shell="expressive"] .gs-updates-heading h1{font:900 clamp(28px,3.2vw,36px)/1.12 var(--gx-font);letter-spacing:-.5px;margin:0 0 8px}
html[data-ui-shell="expressive"] .gs-updates-heading p{color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .gs-updates-filters{gap:12px 20px;padding:20px 0}
html[data-ui-shell="expressive"] .gs-updates-filters label{font-size:13px;font-weight:700;color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .gs-updates-filters select{min-height:44px;border:1px solid var(--gx-ol);border-radius:10px;background-color:var(--gx-card);color:var(--gx-on);font-family:var(--gx-font)}
html[data-ui-shell="expressive"] .gs-updates-filters select:hover:not(:focus){border-color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .gs-updates-filters select:focus{border-color:var(--gx-p);outline:2px solid var(--gx-p);outline-offset:-2px;background-color:var(--gx-card)}
html[data-ui-shell="expressive"] .gs-updates-back{font-weight:700;padding:16px 0;color:var(--gx-p)}
html[data-ui-shell="expressive"] .gs-updates-timeline{display:grid;gap:12px}
html[data-ui-shell="expressive"] .gs-update-entry{border:0;border-radius:16px;background:var(--gx-card);padding:24px;gap:24px}
html[data-ui-shell="expressive"] .gs-update-date{color:var(--gx-on-v);font-weight:600;font-variant-numeric:tabular-nums}
html[data-ui-shell="expressive"] .gs-update-date span{font-size:12px}
html[data-ui-shell="expressive"] .gs-update-content{border-left:0;padding-left:0}
html[data-ui-shell="expressive"] .gs-update-tags span{border-radius:6px;font-size:12px;font-weight:700;padding:4px 8px;background:var(--gx-s2);color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .gs-update-tags .gs-update-major{background:var(--gx-pc);color:var(--gx-on-pc)}
html[data-ui-shell="expressive"] .gs-update-content h2{font-size:22px;font-weight:800;line-height:1.35;margin:14px 0 10px}
html[data-ui-shell="expressive"] .gs-update-content h3{font-size:14px;font-weight:800;color:var(--gx-p)}
html[data-ui-shell="expressive"] .gs-update-content p,html[data-ui-shell="expressive"] .gs-update-content li{color:var(--gx-on-v)}
html[data-ui-shell="expressive"] .gs-update-illustration .va2-preview-screenshot img{border:0;border-radius:12px}
html[data-ui-shell="expressive"] .gs-update-action{font-weight:700;padding:0 18px;border-radius:12px;background:var(--gx-pc);color:var(--gx-on-pc);text-decoration:none;gap:10px;transition:border-radius .4s var(--gx-spring)}
html[data-ui-shell="expressive"] .gs-update-action:hover{border-radius:8px}
html[data-ui-shell="expressive"] .gs-updates [role="alert"] button{min-height:40px;padding:0 18px;border:0;border-radius:12px;background:var(--gx-pc);color:var(--gx-on-pc);font:700 14px var(--gx-font);cursor:pointer}
html[data-ui-shell="expressive"] .gs-updates :focus-visible{outline:3px solid var(--gx-p);outline-offset:3px}
html[data-ui-shell="expressive"] .gs-updates-heading h1:focus{outline:none}
@media (max-width:767px){
html[data-ui-shell="expressive"] .gs-update-entry{grid-template-columns:1fr;gap:6px;padding:18px}
html[data-ui-shell="expressive"] .gs-update-date span{display:inline;margin:0 0 0 8px}
}
@media (prefers-reduced-motion:reduce){
html[data-ui-shell="expressive"] .gs-update-action{transition:none}
}
`);

  const UpdatesContext = React.createContext(null);
  const KINDS = { new: '新功能', improved: '改善', fixed: '修正' };

  function UpdatesProvider({ children }) {
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const alive = React.useRef(true);
    const reload = React.useCallback(async () => {
      setLoading(true);
      setError('');
      if (!window.isRestrictedAccount(window._USER)) {
        // Delivery is idempotent; retry when the user reloads or reconnects.
        api('POST', '/updates/sync-notifications').catch(() => {});
      }
      try {
        const result = await api('GET', '/updates');
        if (alive.current) setItems(result.items || []);
      } catch (_) {
        if (alive.current) setError('暫時無法載入更新消息，請稍後重試。');
      } finally {
        if (alive.current) setLoading(false);
      }
    }, []);
    React.useEffect(() => {
      alive.current = true;
      reload();
      window.addEventListener('online', reload);
      return () => { alive.current = false; window.removeEventListener('online', reload); };
    }, [reload]);
    const dismiss = async (id) => {
      await api('POST', '/updates/' + encodeURIComponent(id) + '/dismiss');
      if (alive.current) setItems(old => old.map(item => item.id === id ? { ...item, dismissed: true } : item));
    };
    return <UpdatesContext.Provider value={{ items, loading, error, reload, dismiss }}>{children}</UpdatesContext.Provider>;
  }

  function UpdatesBanner() {
    const updates = React.useContext(UpdatesContext);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const [hash, setHash] = React.useState(window.location.hash);
    React.useEffect(() => {
      const change = () => setHash(window.location.hash);
      window.addEventListener('hashchange', change);
      return () => window.removeEventListener('hashchange', change);
    }, []);
    const item = updates && updates.items.find(entry => entry.major && !entry.dismissed);
    if (!item || hash.startsWith('#/updates')) return null;
    return <aside className="gs-update-banner" aria-label="重大更新">
      <div><span className="gs-update-label">PORTAL 更新</span><strong>{item.title}</strong>
        <p>{item.summary}</p>{error && <span role="alert">{error}</span>}
      </div>
      <a href={'#/updates/' + encodeURIComponent(item.id)}>看看如何開始 <span aria-hidden="true">→</span></a>
      <button type="button" aria-label="關閉這次更新提醒" disabled={busy} onClick={async () => {
        setBusy(true); setError('');
        try { await updates.dismiss(item.id); } catch (_) { setError('未能儲存關閉狀態，請重試。'); }
        finally { setBusy(false); }
      }}>×</button>
    </aside>;
  }

  function UpdatesPage() {
    const { items, loading, error, reload } = React.useContext(UpdatesContext);
    const [product, setProduct] = React.useState('all');
    const [kind, setKind] = React.useState('all');
    const readId = () => {
      try { return decodeURIComponent((window.location.hash.split('/')[2] || '').split('?')[0]); }
      catch (_) { return ''; }
    };
    const [selected, setSelected] = React.useState(readId);
    const heading = React.useRef(null);
    React.useEffect(() => {
      const change = () => { setSelected(readId()); setProduct('all'); setKind('all'); };
      window.addEventListener('hashchange', change);
      return () => window.removeEventListener('hashchange', change);
    }, []);
    React.useEffect(() => { if (heading.current) heading.current.focus(); }, [selected]);
    const products = [...new Set(items.map(item => item.product))];
    const visible = items.filter(item => selected ? item.id === selected
      : (product === 'all' || item.product === product) && (kind === 'all' || item.kind === kind));
    return <section className="gs-updates">
      <div className="gs-updates-heading">
        <span className="gs-update-label">WHAT’S NEW</span>
        <h1 ref={heading} tabIndex={-1}>更新消息</h1>
        <p>了解新功能、找到熟悉的入口，從這裡開始。</p>
      </div>
      {selected ? <a className="gs-updates-back" href="#/updates">← 所有更新</a> :
        <div className="gs-updates-filters">
          <label>系統 <select aria-label="系統" value={product} onChange={event => setProduct(event.target.value)}>
            <option value="all">所有系統</option>{products.map(name => <option key={name} value={name}>{name}</option>)}
          </select></label>
          <label>類型 <select aria-label="類型" value={kind} onChange={event => setKind(event.target.value)}>
            <option value="all">所有更新</option>{Object.entries(KINDS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select></label>
        </div>}
      {loading ? <p role="status">正在載入更新…</p> : error ? <div role="alert"><p>{error}</p><button type="button" onClick={reload}>重新載入</button></div> :
        <div className="gs-updates-timeline">
          {!visible.length && <p role="status">{selected ? '這則更新不存在，或不適用於你的帳號。' : '目前沒有符合條件的更新。'}</p>}
          {visible.map(item => <article key={item.id} className="gs-update-entry">
            <div className="gs-update-date"><time dateTime={item.date}>{item.date}</time>{item.version && <span>{item.version}</span>}</div>
            <div className="gs-update-content">
              <div className="gs-update-tags"><span>{item.product}</span><span>{KINDS[item.kind] || '更新'}</span>{item.major && <span className="gs-update-major">重大更新</span>}</div>
              <h2><a href={'#/updates/' + encodeURIComponent(item.id)}>{item.title}</a></h2>
              <p>{item.summary}</p>
              {window.FeaturePreviewImage && (window.PORTAL_PREVIEW_IMAGES || {})[item.access] &&
                <div className="gs-update-illustration"><window.FeaturePreviewImage featureKey={item.access} /></div>}
              {item.steps && item.steps.length > 0 && <><h3>從這裡開始</h3><ol>{item.steps.map((step, index) => <li key={index}>{step}</li>)}</ol></>}
              {item.action && <a className="gs-update-action" href={item.action.hash}>{item.action.label} <span aria-hidden="true">→</span></a>}
            </div>
          </article>)}
        </div>}
    </section>;
  }
  window.UpdatesProvider = UpdatesProvider;
  window.UpdatesBanner = UpdatesBanner;
  window.UpdatesPage = UpdatesPage;
})();
